import React, { useMemo, useState } from 'react';
import * as Lucide from 'lucide-react';
import { cn } from '@ideoz/ui';

/**
 * The icons a founder can choose from (UI pass, issue 5).
 *
 * A prototype's icons are Lucide components, so swapping one is a change to the name in the JSX
 * rather than to a class or an attribute. The picker offers the set a prototype actually draws
 * from, searchable by name, and hands back the component name for the editor to write.
 */
export type IconName = string;

/** Names worth offering: the ones prototypes reach for, in the order a founder scans them. */
const SUGGESTED: IconName[] = [
  'Home', 'LayoutDashboard', 'Search', 'Settings', 'User', 'Users', 'Bell', 'Calendar',
  'Clock', 'Mail', 'MessageSquare', 'Phone', 'FileText', 'Folder', 'Download', 'Upload',
  'Plus', 'Minus', 'Check', 'X', 'ChevronRight', 'ArrowRight', 'ArrowUpRight', 'ExternalLink',
  'Heart', 'Star', 'Bookmark', 'Tag', 'Filter', 'SlidersHorizontal', 'Trash2', 'Pencil',
  'Copy', 'Share2', 'Link', 'Lock', 'Shield', 'ShieldCheck', 'AlertTriangle', 'AlertCircle',
  'Info', 'CircleCheck', 'CircleHelp', 'TrendingUp', 'TrendingDown', 'BarChart3', 'PieChart',
  'Activity', 'Package', 'ShoppingCart', 'CreditCard', 'DollarSign', 'Wallet', 'Briefcase',
  'Building2', 'MapPin', 'Truck', 'Stethoscope', 'Pill', 'Syringe', 'HeartPulse', 'Thermometer',
  'ClipboardList', 'BedDouble', 'Boxes', 'Warehouse', 'Zap', 'Sparkles', 'Camera', 'Image',
  'Play', 'Pause', 'RefreshCw', 'LogOut', 'Menu', 'MoreHorizontal', 'Eye', 'EyeOff',
];

function isDrawable(name: string): boolean {
  const candidate = (Lucide as unknown as Record<string, unknown>)[name];
  return typeof candidate === 'function' || typeof candidate === 'object';
}

export function IconPicker({
  current,
  onPick,
  className,
}: {
  /** The icon on the element now, so it can be marked. */
  current?: string | null;
  /** The name to write, and the paths the picker just drew for it, to show in the preview now. */
  onPick: (name: IconName, markup: string) => void;
  className?: string;
}): React.ReactElement {
  const [query, setQuery] = useState('');

  const names = useMemo(() => {
    const available = SUGGESTED.filter(isDrawable);
    const q = query.trim().toLowerCase();
    if (!q) return available;
    // Lucide exports every icon three times - `Rocket`, `RocketIcon`, `LucideRocket` - and all
    // three drew the same picture in the results, so a search for "rock" filled the menu with
    // pairs of the same thing. Only the plain name is offered.
    const all = Object.keys(Lucide).filter(
      (n) => /^[A-Z]/.test(n) && !n.endsWith('Icon') && !n.startsWith('Lucide') && isDrawable(n),
    );
    return all.filter((n) => n.toLowerCase().includes(q)).slice(0, 48);
  }, [query]);

  return (
    <div className={cn('w-[236px] p-1', className)} data-testid="icon-picker">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search icons"
        aria-label="Search icons"
        className="mb-1 h-7 w-full rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-foreground/40"
      />
      <div className="grid max-h-[188px] grid-cols-6 gap-1 overflow-y-auto" role="listbox" aria-label="Icons">
        {names.map((name) => {
          const Glyph = (Lucide as unknown as Record<string, React.ComponentType<{ size?: number }>>)[name];
          return (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === current}
              title={name}
              className={cn(
                'grid h-8 w-8 place-items-center rounded-md border',
                name === current ? 'border-foreground bg-muted' : 'border-transparent hover:bg-black/5 dark:hover:bg-white/10',
              )}
              // The picker has already drawn this icon, so its paths are here to be handed on -
              // no second copy of Lucide, and nothing to render off-screen.
              onClick={(e) => onPick(name, e.currentTarget.querySelector('svg')?.innerHTML ?? '')}
            >
              <Glyph size={15} />
            </button>
          );
        })}
        {names.length === 0 ? (
          <p className="col-span-6 px-1 py-2 text-[11px] text-muted-foreground">No icon by that name.</p>
        ) : null}
      </div>
    </div>
  );
}
